import React, { useState, useEffect } from "react";
import { Spin, message, Tooltip, Button } from "antd";
import PromoCard from "../../components/PromoCard/PromoCard";
import { MinusCircleOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import promosApi from "../../api/promosApi";
import UserTaskProgress from "../../components/UserTaskProgress/UserTaskProgress";

// Скелетон для загрузки карточек
const PromoCardSkeleton = () => (
    <div className="animate-pulse flex flex-col gap-4">
        {[...Array(3)].map((_, idx) => (
            <div
                key={idx}
                className="w-full bg-gray-800 p-4 rounded-lg shadow-md flex items-center gap-4"
            >
                <div className="w-12 h-12 bg-gray-700 rounded-full" />
                <div className="flex flex-col gap-2 w-full">
                    <div className="w-3/4 h-4 bg-gray-700 rounded-md" />
                    <div className="w-1/2 h-4 bg-gray-700 rounded-md" />
                </div>
            </div>
        ))}
    </div>
);

export default function PromoCardLayer({ promos, isLoading, userId, onSubscribed }) {
    const [filters, setFilters] = useState({
        live: false,
        completed: false,
        owned: false,
        nowins: false,
    });
    const [actionId, setActionId] = useState(null);
    const [localPromos, setLocalPromos] = useState(promos);
    const [selectedPromoId, setSelectedPromoId] = useState(null);

    // Обновляем локальный список при изменении внешнего
    useEffect(() => {
        setLocalPromos(promos);
    }, [promos]);

    const toggleFilter = (key) =>
        setFilters((f) => ({ ...f, [key]: !f[key] }));

    // Фильтрация по статусам
    const filtered = localPromos.filter(({ promo }) => {
        if (filters.live && promo.status !== "проводится") return false;
        if (filters.completed && promo.status !== "завершился") return false;
        if (filters.owned && promo.creatorUserId !== userId) return false;
        if (filters.nowins && promo.winnersCount > 0) return false;
        return true;
    });

    // Подписка на промо
    const handleSubscribe = async (promo) => {
        setActionId(promo.id);
        try {
            await promosApi.userPromos.subPromo(userId, promo.id);
            message.success("Пользователь подписан на промо");
            setLocalPromos((prev) => prev.filter((item) => item.promo.id !== promo.id));
            if (typeof onSubscribed === "function") onSubscribed();
        } catch (e) {
            console.error(e);
            message.error("Не удалось подписать на промо");
        } finally {
            setActionId(null);
        }
    };

    // Отписка от промо
    const handleUnsubscribe = async (promo) => {
        setActionId(promo.id);
        try {
            await promosApi.userPromos.unSubPromo(userId, promo.id);
            message.success("Подписка отменена");
            setLocalPromos((prev) => prev.filter((item) => item.promo.id !== promo.id));
            if (typeof onSubscribed === "function") onSubscribed();
        } catch (e) {
            console.error(e);
            message.error("Не удалось отменить подписку");
        } finally {
            setActionId(null);
        }
    };

    // Отображение содержимого выбранного промо
    if (selectedPromoId) {
        return (
            <div className="p-4 bg-gray-900 rounded-lg">
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    className="mb-4 text-white"
                    onClick={() => setSelectedPromoId(null)}
                >
                    Назад
                </Button>
                <UserTaskProgress promoId={selectedPromoId} userId={userId} />
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Фильтры */}
            <div
                className="sticky -top-[8px] z-10 pt-2 pb-2 bg-gray-800 w-full rounded-lg shadow-md"
                style={{ boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)" }}
            >
                <div className="flex flex-wrap items-center justify-center gap-2">
                    {Object.keys(filters).map((key) => (
                        <button
                            key={key}
                            onClick={() => toggleFilter(key)}
                            className={`px-3 py-1 text-[12px] rounded-md border ${
                                filters[key]
                                    ? "bg-green-500 text-white border-green-600"
                                    : "bg-gray-800 text-gray-400 border-gray-700"
                            }`}
                        >
                            {key === "live"
                                ? "Live"
                                : key === "completed"
                                    ? "Completed"
                                    : key === "owned"
                                        ? "Owned"
                                        : "No Wins"}
                        </button>
                    ))}
                </div>
            </div>

            {/* Скелетон */}
            {isLoading ? (
                <PromoCardSkeleton />
            ) : (
                <div className="flex flex-col gap-4 mt-4">
                    {filtered.length === 0 && (
                        <p className="text-gray-400 text-center">
                            Нет промо по выбранным фильтрам
                        </p>
                    )}
                    {filtered.map(({ promo }) => (
                        <div
                            key={promo.id}
                            className="relative"
                        >
                            {/* Spinner overlay */}
                            {actionId === promo.id && (
                                <div className="absolute inset-0 z-20 bg-black bg-opacity-50 flex justify-center items-center rounded-lg">
                                    <Spin />
                                </div>
                            )}

                            {/* Отписка */}
                            <div className="absolute top-2 right-2 z-10">
                                <Tooltip title="Отписаться">
                                    <MinusCircleOutlined
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnsubscribe(promo);
                                        }}
                                        className="text-red-500 hover:text-red-400 text-lg"
                                    />
                                </Tooltip>
                            </div>

                            {/* Карточка промо (нажатие меняет на прогресс) */}
                            <div
                                onClick={() => setSelectedPromoId(promo.id)}
                                className="cursor-pointer"
                            >
                                <PromoCard promo={promo} isLayer={true} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
